// @avantia common-code
import { getFunctionName, SMap, voidFunction } from '@avantia/client-and-server-utilities';
import { HpLoggerFunction } from '@avantia/logging-and-errors';
import {
  getCommonLookupRef,
  getFunctionRef,
  HpFuncRefResolverFunction,
  HpProcessQuestionDynamicPropertyProps,
  HpQuestionLookupItemMap,
  HpQuestionMap,
  HpSectionMap,
  isCommonLookupRef,
  isFunctionRef,
  questionsDynamicPropertyWalker
} from '@avantia/questionset-model';
import { HpClientDebugFlags } from '../clientDebugFlags';
import { logDebug, logInfo, logWarning } from '../clientLogging';
import { registerConfigurationAction } from '../config/registerClientConfig';
import { exceptionToString } from '../tools/clientAndServerUtilities';
import { HpClientPrerequisiteThisReferenceClass } from '../tools/prerequisiteThisReference';
import { createEmptyDisplayState } from './initialState';

export interface HpQuestionScriptResolverProps {
  commonLookups: SMap<HpQuestionLookupItemMap>;
  debugFlag: HpClientDebugFlags;
  funcRefResolver: HpFuncRefResolverFunction;
  productId: string;
  questions: HpQuestionMap;
  sections: HpSectionMap;
}

let optimisationEnabled: boolean, clientDebugFlags: HpClientDebugFlags;
registerConfigurationAction((config) => {
  ({ optimisationEnabled, clientDebugFlags } = config);
});

export function questionScriptResolver({
  commonLookups,
  debugFlag,
  funcRefResolver,
  productId,
  questions,
  sections
}: HpQuestionScriptResolverProps): void {
  const debugOut = (debugFlag & clientDebugFlags) > 0 ? output : voidFunction;
  const self = !optimisationEnabled
    ? undefined
    : new HpClientPrerequisiteThisReferenceClass({
        answers: {},
        commonLookups,
        debugFlag,
        displayState: createEmptyDisplayState({ addBlankSection: false }),
        groupNumber: undefined,
        indexes: undefined,
        productId,
        questions,
        sections,
        sourceQuestionId: '',
        sourceQuestionType: undefined
      });

  function resolveScriptReference<ParentT, GrandParentT>({
    expr,
    propertyName,
    index,
    parent
  }: HpProcessQuestionDynamicPropertyProps<ParentT, GrandParentT>): void {
    // eslint-disable-next-line @typescript-eslint/ban-types
    let func: Function | undefined;
    const exprType = typeof expr;
    if (exprType === 'string') {
      if (
        propertyName === 'helpText' ||
        propertyName === 'infoText' ||
        propertyName === 'text' ||
        propertyName === 'questionText' ||
        propertyName === 'buttonText' ||
        propertyName === 'subtext' ||
        propertyName === 'warningText'
      ) {
        return;
      }

      if (isFunctionRef(expr)) {
        func = funcRefResolver(getFunctionRef(expr) as string);
      } else if (isCommonLookupRef(expr)) {
        const commonLookup = commonLookups[getCommonLookupRef(expr) as string];
        if (commonLookup) {
          func = (): HpQuestionLookupItemMap => commonLookup;
        }
      } else {
        throw new Error(`Unexpected expression (of type '${exprType}'): "${expr}"`);
      }
    } else if (exprType === 'function') {
      func = expr as any; // only happens when switching question set
    }

    if (func) {
      if (propertyName && index !== undefined) {
        debugOut(expr, parent[propertyName][index], func);
        parent[propertyName][index] = func;
      } else if (propertyName) {
        debugOut(expr, parent[propertyName], func);
        parent[propertyName] = func as any;
      } else if (index !== undefined) {
        debugOut(expr, parent[index], func);
        parent[index] = func;
      }

      if (
        optimisationEnabled &&
        (propertyName === 'minValueExpr' || propertyName === 'maxValueExpr' || propertyName === 'defaultExpr')
      ) {
        // These are the expression properties that should be safe to evaluate once and save the result.
        let propName = propertyName as string;
        propName = propName.substring(0, propName.length - 4);
        try {
          // eslint-disable-next-line @typescript-eslint/ban-types
          const valOrFunc = (func as Function).call(self);
          if (typeof valOrFunc !== 'function') {
            parent[propName] = valOrFunc;
            delete parent[propertyName];
          } else {
            parent[propertyName] = valOrFunc;
          }
        } catch (ex) {
          logWarning(
            `The ${propName} expression could not be evaluated without context: ${exceptionToString(ex)}\n${func}`,
            debugFlag
          );
        }
      }
    } else if (typeof expr !== 'function') {
      throw new Error(`Unhandled expression "${expr}".`);
    }
  }

  const logWalker: HpLoggerFunction = (msg) => logDebug(msg, HpClientDebugFlags.PayloadMapper);
  questionsDynamicPropertyWalker({
    questions,
    processor: resolveScriptReference as any,
    funcCache: undefined,
    logInfo: logWalker
  });
}

// eslint-disable-next-line @typescript-eslint/ban-types
function output(expr: string, value: any, func: Function): void {
  logInfo(`"${expr}" -> "${value}" -> ${getFunctionName(func)}`, HpClientDebugFlags.PayloadMapper);
}
